import { useState } from 'react';
import { SavedFilter } from '../hooks/usePersistentFilters';
import { formatDate } from '../utils/dateFormatter';
import Modal from './Modal';
import './SavedFiltersPanel.css';

interface SavedFiltersPanelProps {
  savedFilters: SavedFilter[];
  activeFilterId?: string;
  onApply: (filter: SavedFilter) => void;
  onDelete: (filterId: string) => void;
  onSave: (name: string, isDefault: boolean) => void;
  onClose: () => void;
  isOpen: boolean;
}

export default function SavedFiltersPanel({
  savedFilters,
  activeFilterId,
  onApply,
  onDelete,
  onSave,
  onClose,
  isOpen,
}: SavedFiltersPanelProps) {
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [isDefault, setIsDefault] = useState(false);

  const handleSave = () => {
    if (filterName.trim()) {
      onSave(filterName.trim(), isDefault);
      setFilterName('');
      setIsDefault(false);
      setShowSaveModal(false);
    }
  };

  const defaultFilter = savedFilters.find(f => f.isDefault);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title="Filtros Guardados">
        <div className="saved-filters-panel">
          <div className="saved-filters-header">
            <button
              className="btn btn-primary"
              onClick={() => setShowSaveModal(true)}
            >
              💾 Guardar Filtros Actuales
            </button>
          </div>

          {savedFilters.length === 0 ? (
            <div className="saved-filters-empty">
              <p>No hay filtros guardados</p>
              <p className="saved-filters-hint">
                Guarda tus filtros favoritos para aplicarlos rápidamente
              </p>
            </div>
          ) : (
            <div className="saved-filters-list">
              {savedFilters.map((filter) => (
                <div
                  key={filter.id}
                  className={`saved-filter-item ${
                    activeFilterId === filter.id ? 'active' : ''
                  } ${filter.isDefault ? 'default' : ''}`}
                >
                  <div className="saved-filter-info">
                    <div className="saved-filter-name">
                      {filter.name}
                      {filter.isDefault && (
                        <span className="saved-filter-badge">Por defecto</span>
                      )}
                    </div>
                    <div className="saved-filter-date">
                      {formatDate(filter.createdAt)}
                    </div>
                  </div>
                  <div className="saved-filter-actions">
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => onApply(filter)}
                    >
                      Aplicar
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => onDelete(filter.id)}
                      title="Eliminar filtro"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={showSaveModal}
        onClose={() => {
          setShowSaveModal(false);
          setFilterName('');
          setIsDefault(false);
        }}
        title="Guardar Filtros"
      >
        <div className="save-filter-form">
          <div className="input-group">
            <label className="input-label">Nombre del filtro</label>
            <input
              type="text"
              className="input"
              placeholder="Ej: Stock bajo en Taller"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSave();
                }
              }}
              autoFocus
            />
          </div>
          <div className="input-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
              />
              <span>Usar como filtro por defecto</span>
            </label>
            {defaultFilter && isDefault && (
              <p className="save-filter-warning">
                ⚠️ Esto reemplazará el filtro por defecto actual: "{defaultFilter.name}"
              </p>
            )}
          </div>
          <div className="save-filter-actions">
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowSaveModal(false);
                setFilterName('');
                setIsDefault(false);
              }}
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={!filterName.trim()}
            >
              Guardar
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

